import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, ArrowLeft, CheckCircle } from 'lucide-react';
import passwordResetService from '../services/passwordResetService';
import { useToast } from '../hooks/useToast';

type Step = 'email' | 'otp' | 'password' | 'success';

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();

  // Step management
  const [step, setStep] = useState<Step>('email');

  // Form data
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // UI states
  const [loading, setLoading] = useState(false);
  const [expiresInSeconds, setExpiresInSeconds] = useState(0);

  // Step 1: Request OTP
  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error('Vui lòng nhập email');
      return;
    }

    setLoading(true);
    try {
      const response = await passwordResetService.requestOtp(email);
      toast.success('Mã OTP đã được gửi', response.message);
      setExpiresInSeconds(response.expiresInSeconds);
      setStep('otp');
    } catch (error: any) {
      toast.error(
        'Gửi mã OTP thất bại',
        error.response?.data?.message || 'Không thể gửi mã OTP. Vui lòng thử lại.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify OTP and move to password step
  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();

    if (otpCode.length !== 6) {
      toast.error('Mã OTP phải có 6 chữ số');
      return;
    }

    setLoading(true);
    try {
      const response = await passwordResetService.verifyOtp(email, otpCode);
      
      if (response.isValid) {
        toast.success('Mã OTP hợp lệ');
        setStep('password');
      } else {
        toast.error('Mã OTP không hợp lệ', response.message);
      }
    } catch (error: any) {
      toast.error(
        'Xác thực thất bại',
        error.response?.data?.message || 'Mã OTP không hợp lệ hoặc đã hết hạn'
      );
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Reset Password
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (newPassword.length < 6) {
      toast.error('Mật khẩu phải có ít nhất 6 ký tự');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Mật khẩu xác nhận không khớp');
      return;
    }

    setLoading(true);
    try {
      const response = await passwordResetService.resetPassword(
        email,
        otpCode,
        newPassword
      );

      if (response.success) {
        toast.success('Đặt lại mật khẩu thành công!');
        setStep('success');
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/auth?mode=login');
        }, 3000);
      }
    } catch (error: any) {
      toast.error(
        'Đặt lại mật khẩu thất bại',
        error.response?.data?.message || 'Không thể đặt lại mật khẩu. Vui lòng thử lại.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP input (only numbers, max 6 digits)
  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtpCode(value);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back button */}
        <Link
          to="/auth?mode=login"
          className="inline-flex items-center gap-2 text-slate-600 hover:text-blue-600 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Quay lại đăng nhập</span>
        </Link>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl border border-blue-100 p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl mb-4 shadow-lg">
              {step === 'success' ? (
                <CheckCircle className="w-10 h-10 text-white" />
              ) : step === 'password' ? (
                <Lock className="w-10 h-10 text-white" />
              ) : (
                <Mail className="w-10 h-10 text-white" />
              )}
            </div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent mb-2">
              {step === 'email' && 'Quên Mật Khẩu'}
              {step === 'otp' && 'Xác Thực OTP'}
              {step === 'password' && 'Đặt Mật Khẩu Mới'}
              {step === 'success' && 'Thành Công!'}
            </h1>
            <p className="text-slate-600">
              {step === 'email' && 'Nhập email để nhận mã OTP'}
              {step === 'otp' && `Mã OTP đã được gửi đến ${email}`}
              {step === 'password' && 'Nhập mật khẩu mới của bạn'}
              {step === 'success' && 'Mật khẩu đã được đặt lại thành công'}
            </p>
          </div>

          {/* Step 1: Email Input */}
          {step === 'email' && (
            <form onSubmit={handleRequestOtp} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your.email@example.com"
                    className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-cyan-600 transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 shadow-lg hover:shadow-xl"
              >
                {loading ? 'Đang gửi...' : 'Gửi mã OTP'}
              </button>
            </form>
          )}

          {/* Step 2: OTP Input */}
          {step === 'otp' && (
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Mã OTP (6 chữ số)
                </label>
                <input
                  type="text"
                  value={otpCode}
                  onChange={handleOtpChange}
                  placeholder="000000"
                  className="w-full px-4 py-4 border-2 border-blue-200 rounded-xl text-center text-3xl font-mono tracking-[0.5em] focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-blue-50/50"
                  maxLength={6}
                  required
                  disabled={loading}
                  autoFocus
                />
                <p className="text-sm text-blue-600 mt-3 text-center font-medium">
                  ⏱️ Mã OTP có hiệu lực trong {Math.floor(expiresInSeconds / 60)} phút
                </p>
              </div>

              <button
                type="submit"
                disabled={loading || otpCode.length !== 6}
                className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-cyan-600 transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 shadow-lg hover:shadow-xl"
              >
                {loading ? 'Đang xác thực...' : 'Xác thực OTP'}
              </button>

              <button
                type="button"
                onClick={() => {
                  setStep('email');
                  setOtpCode('');
                }}
                className="w-full text-blue-600 hover:text-blue-700 py-2 text-sm transition-colors font-medium"
                disabled={loading}
              >
                🔄 Gửi lại mã OTP
              </button>
            </form>
          )}

          {/* Step 3: New Password */}
          {step === 'password' && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Mật khẩu mới
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Nhập mật khẩu mới"
                    className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    minLength={6}
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Xác nhận mật khẩu
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Nhập lại mật khẩu"
                    className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    minLength={6}
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-800">
                  💡 Mật khẩu phải có ít nhất 6 ký tự
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-cyan-600 transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 shadow-lg hover:shadow-xl"
              >
                {loading ? 'Đang xử lý...' : 'Đặt lại mật khẩu'}
              </button>
            </form>
          )}

          {/* Step 4: Success */}
          {step === 'success' && (
            <div className="text-center space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-green-800 font-medium">
                  Mật khẩu của bạn đã được đặt lại thành công!
                </p>
                <p className="text-green-600 text-sm mt-2">
                  Bạn sẽ được chuyển đến trang đăng nhập sau 3 giây...
                </p>
              </div>

              <button
                onClick={() => navigate('/auth?mode=login')}
                className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-cyan-600 transition-all transform hover:scale-[1.02] shadow-lg hover:shadow-xl"
              >
                Đăng nhập ngay
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-slate-600 text-sm mt-6">
          Bạn đã có tài khoản?{' '}
          <Link
            to="/auth?mode=login"
            className="text-blue-600 hover:text-blue-700 font-semibold"
          >
            Đăng nhập
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
